import { PortfolioAllocationGrid } from '@components/PortfolioAllocationGrid';
import {
  buildAssetClassGroupView,
  buildPortfolioAllocationView,
} from '@components/PortfolioAllocationGrid/buildPortfolioAllocationView';
import { useCollapsedAssetGroups } from '@components/PortfolioAllocationGrid/useCollapsedAssetGroups';
import { isAssetClassAllocationModel } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import type { PortfolioSubAccount } from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import { isTradeableAllocationMember } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import type { AccountAllocationComparison } from '@ovComponents/4-module/workflowCompletion/subSteps/editAccountAllocations/reviewAccountAllocationsModel';
import { Stack, Typography } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import { AccountAllocationLeftHead } from './AccountAllocationLeftHead';
import {
  buildAccountAllocationComparisonSlots,
  COMPARISON_COLUMNS_PER_ACCOUNT,
} from './buildAccountAllocationComparisonSlots';
import { accountAllocationRows } from './buildAccountAllocationSlots';
import type { AccountAllocationGridReport } from './types';

export type AccountAllocationComparisonGridProp = {
  report: AccountAllocationGridReport;
  comparison: AccountAllocationComparison;
};

// Accounts that can't be allocated aren't staged or signed, so they're left out.
const isAllocatedAccount = (subAccount: PortfolioSubAccount) =>
  isTradeableAllocationMember(subAccount);

export const AccountAllocationComparisonGrid = ({
  report,
  comparison,
}: AccountAllocationComparisonGridProp) => {
  const { t } = useTranslation();
  const { translateBackendString } = useLocalization();
  const { collapsedAssetGroups, toggleAssetGroupCollapse } =
    useCollapsedAssetGroups();
  const isAssetClassPortfolio = isAssetClassAllocationModel(report);

  const { subAccounts, securities } = useMemo(
    () =>
      buildPortfolioAllocationView({
        portfolioReport: report,
        t,
        translateBackendString,
      }),
    [report, t, translateBackendString],
  );
  const rows = useMemo(() => accountAllocationRows(securities), [securities]);
  const assetClassGroups = useMemo(
    () =>
      buildAssetClassGroupView({
        securities: rows,
        isAssetClassPortfolio,
        t,
        translateBackendString,
      }),
    [rows, isAssetClassPortfolio, t, translateBackendString],
  );
  const offTargetFunds = comparison.fundTotals
    .filter(({ withinTolerance }) => !withinTolerance)
    .map(({ financialProductId }) => {
      const security = rows.find(({ id }) => id === financialProductId);
      return security?.ticker || security?.translatedName || financialProductId;
    });
  const hasNonTradeableAccount = !subAccounts.every(isAllocatedAccount);

  return (
    <Stack spacing={2} useFlexGap>
      {hasNonTradeableAccount && (
        <Typography variant='bodySmall' component='p'>
          {t('components:accountAllocationGrid.nonTradeableNote')}
        </Typography>
      )}
      <PortfolioAllocationGrid
        securities={rows}
        subAccounts={subAccounts}
        assetClassGroups={assetClassGroups}
        isSubAccountVisible={isAllocatedAccount}
        showTargetColumn={false}
        collapsedAssetGroups={collapsedAssetGroups}
        onToggleAssetGroup={toggleAssetGroupCollapse}
        columnsPerSubAccount={COMPARISON_COLUMNS_PER_ACCOUNT}
        leftTableHead={
          <AccountAllocationLeftHead
            isAssetClassPortfolio={isAssetClassPortfolio}
            withSubHeadRow
          />
        }
        slots={buildAccountAllocationComparisonSlots({
          report,
          securities: rows,
          isAssetClassPortfolio,
          comparison,
          t,
        })}
      />
      {offTargetFunds.length > 0 && (
        <Typography variant='bodySmall' component='p'>
          {t('components:accountAllocationGrid.comparison.fundsOffTarget', {
            funds: offTargetFunds.join(', '),
          })}
        </Typography>
      )}
    </Stack>
  );
};
